/**
 * „Jetzt übertragen“ — ohne dass Ablage, Einstellungsdialog und Takt einander kennen.
 *
 * Vorgabe des Nutzers (2026-10-02, abends): gepuffert wird **immer**, gesendet wird **im Takt**
 * — alle zehn Sekunden ein Paket mit allem, was sich angesammelt hat (`server/takt.ts`,
 * `server/classic/takt.ts`). ⛔ Bis dahin stiess jede Ablage sofort einen Durchgang an
 * (`stosseAn`, seit dem Vormittag desselben Tages); ohne Netz hiess das ein Fehlversuch je
 * Klick, 42 Netzfehlerzeilen in 160 Sekunden. Das ist entfallen.
 *
 * Geblieben ist der Weg von Hand: die Takte melden sich mit `meldeWecker` an, und
 * `uebertrageAlles()` weckt sie sofort — auch bei abgeschaltetem `auto_upload`.
 *
 * `auto_upload` aus (nur iPad-Einstellungen): der Takt läuft weiter für Herzschlag,
 * Anforderungen des Prüfungsleiters und Gerätebefehle, aber Bewertungen und Dateien gehen nur
 * über „Jetzt übertragen“ hinaus.
 */

/** `manuell` heisst: von Hand ausgelöst, sendet auch bei abgeschaltetem `auto_upload`. */
export type Wecker = (manuell: boolean) => Promise<void>;

const wecker = new Set<Wecker>();
let autoQuelle: (() => boolean) | null = null;

export function meldeWecker(w: Wecker): () => void {
  wecker.add(w);
  return () => {
    wecker.delete(w);
  };
}

/** „Jetzt übertragen“: alles, auch Dateien, auch bei abgeschaltetem `auto_upload`. */
export async function uebertrageAlles(): Promise<void> {
  await Promise.all([...wecker].map((w) => w(true)));
}

/** Angemeldet aus `main.tsx`, damit dieses Modul den Anwendungsspeicher nicht kennt. */
export function setzeAutoUploadQuelle(quelle: (() => boolean) | null): void {
  autoQuelle = quelle;
}

export function autoUpload(): boolean {
  return autoQuelle ? autoQuelle() : true;
}

/**
 * Macht aus einem Durchgang einen, der nie in sich selbst hineinläuft und nichts verliert.
 *
 * Läuft schon einer, wird der Anstoss **gemerkt** statt verworfen: was während eines
 * Durchgangs abgelegt wurde, hat der laufende womöglich nicht mehr gesehen. Mehrere Anstösse
 * währenddessen ergeben **einen** Nachlauf; war einer davon von Hand, ist es auch der Nachlauf.
 */
export function wiederholeBisRuhe(
  durchgang: (manuell: boolean) => Promise<void>,
): (manuell: boolean) => Promise<void> {
  let laufend: Promise<void> | null = null;
  let nachlauf: { manuell: boolean } | null = null;
  let nachlaufFertig: Promise<void> | null = null;

  const starte = (manuell: boolean): Promise<void> => {
    laufend = durchgang(manuell).finally(() => {
      laufend = null;
    });
    return laufend;
  };

  return (manuell) => {
    if (!laufend) return starte(manuell);
    nachlauf = { manuell: (nachlauf?.manuell ?? false) || manuell };
    if (!nachlaufFertig) {
      nachlaufFertig = laufend
        .catch(() => undefined)
        .then(() => {
          const n = nachlauf!;
          nachlauf = null;
          nachlaufFertig = null;
          return starte(n.manuell);
        });
    }
    return nachlaufFertig;
  };
}
